import * as THREE from 'three';
import { CORNERS, elevationToY } from '../map/HexMetrics';
import { axialToWorld } from '../map/HexCoordinates';
import { HexCellData, cornerElevation, waterLevel } from '../map/HexSurface';

/**
 * Red overlay on the hovered cell.
 *
 * One mesh of seven vertices, rewritten in place whenever the hover moves —
 * building a fresh geometry per mouse move would churn buffers sixty times a
 * second for six triangles.
 *
 * It reproduces the surface it sits on rather than being a flat hexagon: the
 * same interpolated corner heights as the terrain, or the flat sea surface over
 * water. A flat quad would sink into any slope.
 */

/** Enough to clear the surface, far below one elevation step (0.25). */
const LIFT = 0.01;

const HIGHLIGHT_COLOR = 0xff2d2d;

export class HexHighlight {
  readonly object: THREE.Mesh;
  private readonly positions: THREE.Float32BufferAttribute;

  constructor() {
    const geometry = new THREE.BufferGeometry();

    // Centre first, then the six corners; the fan winds the same way as the
    // terrain so the face points up
    this.positions = new THREE.Float32BufferAttribute(new Float32Array(7 * 3), 3);
    this.positions.setUsage(THREE.DynamicDrawUsage);
    geometry.setAttribute('position', this.positions);

    const indices: number[] = [];
    for (let corner = 0; corner < 6; corner++) {
      indices.push(0, 1 + corner, 1 + ((corner + 1) % 6));
    }
    geometry.setIndex(indices);

    // Unlit, so the highlight reads as interface rather than as terrain that
    // happens to be red. depthWrite off keeps it from occluding anything.
    const material = new THREE.MeshBasicMaterial({
      color: HIGHLIGHT_COLOR,
      transparent: true,
      opacity: 0.45,
      depthWrite: false,
    });

    this.object = new THREE.Mesh(geometry, material);
    this.object.visible = false;
    this.object.renderOrder = 2;

    // Three.js builds the bounding sphere once, the first time it needs one, and
    // rewriting the positions in place never invalidates it. The sphere would
    // stay frozen on whichever cell was highlighted first, and the overlay would
    // then be culled everywhere else — the smaller the frustum, the sooner that
    // stale point leaves it, so the effect shows up as the highlight vanishing
    // when zoomed in. Recomputing it per hover would be the other fix, but for
    // one mesh of six triangles the culling test costs more than the draw.
    this.object.frustumCulled = false;
  }

  /**
   * Repaint it.
   *
   * The overlay answers two different questions in two different modes — which
   * hex is this, and would a building go here — and the colour is what tells
   * them apart. One class, because the shape is the same fan sitting on the
   * same interpolated surface, and a second copy of that would drift.
   */
  setColor(color: number): void {
    (this.object.material as THREE.MeshBasicMaterial).color.setHex(color);
  }

  showAt(world: Map<number, HexCellData>, cell: HexCellData): void {
    const { x, z } = axialToWorld(cell.q, cell.r);
    // The surface the cursor is actually over: a lake stands at its own height,
    // so highlighting it at sea level would sink the overlay to the bottom of
    // whatever valley the lake sits in.
    const water = waterLevel(cell);

    const centerY = elevationToY(water ?? cell.elevation);
    this.positions.setXYZ(0, x, centerY + LIFT, z);

    for (let corner = 0; corner < 6; corner++) {
      const offset = CORNERS[corner];
      const elevation = water ?? cornerElevation(world, cell, corner);

      this.positions.setXYZ(
        1 + corner,
        x + offset.x,
        elevationToY(elevation) + LIFT,
        z + offset.z
      );
    }

    this.positions.needsUpdate = true;
    this.object.visible = true;
  }

  hide(): void {
    this.object.visible = false;
  }

  dispose(): void {
    this.object.geometry.dispose();
    (this.object.material as THREE.Material).dispose();
  }
}
